import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  TextField,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import ExitToAppRoundedIcon from '@mui/icons-material/ExitToAppRounded';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { useLingui } from '@lingui/react';
import { t } from '@lingui/core/macro';
import { signOut } from 'firebase/auth';
import { getApiUrl } from '../utils/api';
import { authFetch } from '../utils/authFetch';
import { auth } from '../firebase';
import { Project } from '../contexts/ProjectContext';
import LanguageSwitcher from '../components/LanguageSwitcher';
import AppearanceToggle from '../components/AppearanceToggle';
import ProjectCreationWizard from '../components/ProjectCreationWizard';
import './ProjectSelector.css';
import { trackEvent, trackPageView } from '../statsig';
import { Skeleton, SkeletonButton, SkeletonGrid, SkeletonPage, SkeletonText } from '../components/Skeleton';

interface ProjectSelectorProps {
  onProjectSelect: (project: Project, isNewProject?: boolean) => void;
}

interface RenameDialogState {
  projectId: string;
  currentName: string;
  draftName: string;
}

type ProjectSort = 'last-edited' | 'name-asc' | 'name-desc';

const ProjectSelector: React.FC<ProjectSelectorProps> = ({ onProjectSelect }) => {
  const { i18n: linguiI18n } = useLingui();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [createDialogOpen, setCreateDialogOpen] = useState<boolean>(false);
  const [removingProjectIds, setRemovingProjectIds] = useState<string[]>([]);
  const [renamingProjectId, setRenamingProjectId] = useState<string | null>(null);
  const [renameDialog, setRenameDialog] = useState<RenameDialogState | null>(null);
  const [projectMenuAnchor, setProjectMenuAnchor] = useState<HTMLElement | null>(null);
  const [menuProject, setMenuProject] = useState<Project | null>(null);
  const [isProjectMenuOpen, setIsProjectMenuOpen] = useState<boolean>(false);
  const [projectSearch, setProjectSearch] = useState<string>('');
  const [projectSort, setProjectSort] = useState<ProjectSort>('last-edited');

  const handleLogout = async (): Promise<void> => {
    await signOut(auth);
  };

  const fetchProjects = useCallback(async (): Promise<void> => {
    try {
      setLoading(true);
      setError('');
      const response = await authFetch(`${getApiUrl()}/projects`);
      const data = await response.json();

      if (data.status === 'OK') {
        setProjects(data.projects);
      } else {
        setError(data.message || t`Failed to fetch projects`);
      }
    } catch (err) {
      setError(t`Failed to load projects. Please try again.`);
      console.error('Error fetching projects:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    trackPageView('project_selector');
  }, []);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const handleDeleteProject = async (projectId: string): Promise<void> => {
    if (!window.confirm(t`Are you sure you want to delete this project? This action cannot be undone.`)) return;

    // Trigger animation
    setRemovingProjectIds((prev) => [...prev, projectId]);

    setTimeout(async () => {
      try {
        const response = await authFetch(`${getApiUrl()}/projects/${projectId}`, {
          method: 'DELETE',
        });

        const data = await response.json();

        if (data.status === 'OK') {
          trackEvent('project_deleted', {
            project_id: projectId,
          });
          setProjects((prev) => prev.filter((p) => p.id !== projectId));
        } else {
          setError(data.message || t`Failed to delete project`);
        }
      } catch (err) {
        setError(t`Failed to delete project. Please try again.`);
        console.error('Error deleting project:', err);
      } finally {
        setRemovingProjectIds((prev) => prev.filter((id) => id !== projectId));
      }
    }, 300); // match CSS transition duration
  };

  const handleLeaveProject = async (projectId: string): Promise<void> => {
    if (!window.confirm(t`Are you sure you want to leave this project? You will no longer be able to access it.`)) return;

    setRemovingProjectIds((prev) => [...prev, projectId]);
    try {
      const response = await authFetch(`${getApiUrl()}/projects/${projectId}/membership`, {
        method: 'DELETE',
      });
      const data = await response.json();

      if (data.status === 'OK') {
        trackEvent('project_left', { project_id: projectId });
        setProjects((prev) => prev.filter((project) => project.id !== projectId));
      } else {
        setError(data.message || t`Failed to leave project`);
      }
    } catch (err) {
      setError(t`Failed to leave project. Please try again.`);
      console.error('Error leaving project:', err);
    } finally {
      setRemovingProjectIds((prev) => prev.filter((id) => id !== projectId));
    }
  };

  const handleOpenRenameDialog = (project: Project): void => {
    if (!project.sheet_id) {
      window.alert(t`You must first select the project due to technical limitations.`);
      return;
    }

    setRenameDialog({
      projectId: project.id,
      currentName: project.name,
      draftName: project.name,
    });
    setError('');
  };

  const handleCloseRenameDialog = (): void => {
    setRenameDialog(null);
  };

  const handleOpenCreateDialog = (): void => {
    setError('');
    setCreateDialogOpen(true);
  };

  const handleCloseCreateDialog = (): void => {
    setCreateDialogOpen(false);
  };

  const handleCreatedProject = (project: Project): void => {
    setCreateDialogOpen(false);
    onProjectSelect(project, true);
  };

  const handleOpenProjectMenu = (event: React.MouseEvent<HTMLElement>, project: Project): void => {
    setProjectMenuAnchor(event.currentTarget);
    setMenuProject(project);
    setIsProjectMenuOpen(true);
  };

  const handleCloseProjectMenu = (): void => {
    setIsProjectMenuOpen(false);
  };

  const handleProjectMenuExited = (): void => {
    setProjectMenuAnchor(null);
    setMenuProject(null);
  };

  const handleRenameProjectSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!renameDialog) return;

    const newName: string = renameDialog.draftName.trim();
    if (!newName) return;
    if (newName === renameDialog.currentName) {
      handleCloseRenameDialog();
      return;
    }

    try {
      setError('');
      setRenamingProjectId(renameDialog.projectId);

      const response = await authFetch(`${getApiUrl()}/projects/${renameDialog.projectId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ projectId: renameDialog.projectId, newName }),
      });

      const data = await response.json();

      if (data.status === 'OK') {
        trackEvent('project_renamed', {
          project_id: renameDialog.projectId,
          previous_name: renameDialog.currentName,
          next_name: newName,
        });
        setProjects((prev) =>
          prev.map((project) =>
            project.id === renameDialog.projectId ? { ...project, name: newName } : project
          )
        );
        handleCloseRenameDialog();
      } else {
        setError(data.message || t`Failed to rename project`);
      }
    } catch (err) {
      setError(t`Failed to rename project. Please try again.`);
      console.error('Error renaming project:', err);
    } finally {
      setRenamingProjectId(null);
    }
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const locale = linguiI18n.locale === 'he'
      ? 'he-IL'
      : linguiI18n.locale === 'pl'
        ? 'pl-PL'
        : 'en-US';
    return date.toLocaleDateString(locale, { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const getProjectRoleLabel = (permissionTier: Project['permission_tier']): string => {
    if (permissionTier === 1) return t`Owner`;
    if (permissionTier === 2) return t`Editor`;
    return t`Data Only`;
  };

  const visibleProjects = useMemo(() => {
    const normalizedSearch = projectSearch.trim().toLocaleLowerCase();
    const filteredProjects = projects.filter((project) =>
      project.name.toLocaleLowerCase().includes(normalizedSearch)
    );

    return [...filteredProjects].sort((left, right) => {
      if (projectSort === 'name-asc') return left.name.localeCompare(right.name);
      if (projectSort === 'name-desc') return right.name.localeCompare(left.name);
      return new Date(right.last_edited_at ?? right.created_at).getTime()
        - new Date(left.last_edited_at ?? left.created_at).getTime();
    });
  }, [projectSearch, projectSort, projects]);

  if (loading) {
    return (
      <div className="project-selector project-selector-loading">
        <div className="project-selector-language">
          <AppearanceToggle />
          <LanguageSwitcher />
          <button
            type="button"
            className="language-menu-button"
            onClick={handleLogout}
            aria-label={t`Logout`}
            title={t`Logout`}
          >
            <span className="material-symbols-outlined" aria-hidden="true">
              logout
            </span>
          </button>
        </div>
        <div className="project-selector-container">
          <SkeletonPage variant="content" label={String(t`Loading projects...`)}>
            <Skeleton width="220px" height={32} radius={5} className="skeleton-surface-dark" />
            <SkeletonText lines={2} widths={['62%', '42%']} lineHeight={12} gap={10} className="skeleton-surface-dark" />
            <div className="project-selector-loading-actions">
              <SkeletonButton width={150} height={44} className="skeleton-surface-dark" />
            </div>
            <SkeletonGrid count={4} dark className="project-skeleton-grid" />
          </SkeletonPage>
        </div>
      </div>
    );
  }

  return (
    <div className="project-selector">
      <div className="project-selector-language">
        <AppearanceToggle />
        <LanguageSwitcher />
        <button
          type="button"
          className="language-menu-button"
          onClick={handleLogout}
          aria-label={t`Logout`}
          title={t`Logout`}
        >
          <span className="material-symbols-outlined" aria-hidden="true">
            logout
          </span>
        </button>
      </div>
      <div className="project-selector-container">
        <div className="project-selector-heading">
          <div>
            <p className="project-selector-eyebrow">{t`FinTitles workspace`}</p>
            <h1>{t`Your projects`}</h1>
            <p className="subtitle">{t`Choose a project to continue, or start a new credits workspace.`}</p>
          </div>
          <button type="button" className="create-button" onClick={handleOpenCreateDialog}>
            <AddRoundedIcon aria-hidden="true" />
            {t`New Project`}
          </button>
        </div>

        {error && <div className="error-message">{error}</div>}

        {projects.length > 0 && (
          <div className="project-list-controls">
            <input
              type="search"
              placeholder={t`Search projects`}
              value={projectSearch}
              onChange={(event) => setProjectSearch(event.target.value)}
              className="project-name-input project-search"
              aria-label={t`Search projects`}
            />
            <TextField
              select
              size="small"
              label={t`Sort by`}
              value={projectSort}
              onChange={(event) => setProjectSort(event.target.value as ProjectSort)}
              className="project-sort"
              SelectProps={{
                MenuProps: {
                  slotProps: { paper: { className: 'project-sort-menu' } },
                },
              }}
            >
              <MenuItem value="last-edited">{t`Last edited`}</MenuItem>
              <MenuItem value="name-asc">{t`Name (A–Z)`}</MenuItem>
              <MenuItem value="name-desc">{t`Name (Z–A)`}</MenuItem>
            </TextField>
          </div>
        )}

        <div className="projects-list">
          {projects.length === 0 ? (
            <div className="no-projects">
              <p>{t`You don't have any projects yet.`}</p>
              <p>{t`Create one to get started!`}</p>
            </div>
          ) : visibleProjects.length === 0 ? (
            <div className="no-projects" role="status">
              <p>{t`No projects found.`}</p>
              <p>{t`Try a different search term.`}</p>
            </div>
          ) : (
            visibleProjects.map((project) => {
              const isRenaming: boolean = renamingProjectId === project.id;
              return (
              <article
                  key={project.id}
                  className={`project-card ${removingProjectIds.includes(project.id) ? 'removing' : ''}`}
                >
                  <div className="project-info">
                    <h3>{project.name}</h3>
                    <div className="project-meta">
                      <p className="project-date">{t`Created`}{linguiI18n.locale === 'he' ? ' ב־' : ' '}<bdi>{formatDate(project.created_at)}</bdi></p>
                      <p className="project-role">{getProjectRoleLabel(project.permission_tier)}</p>
                    </div>
                  </div>
                  <div className="project-actions">
                    <button
                      onClick={() => {
                        trackEvent('project_selected', {
                          project_id: project.id,
                          project_name: project.name,
                        });
                        onProjectSelect(project);
                      }}
                      className="select-button"
                    >
                      {t`Open`}
                    </button>
                    <IconButton
                      centerRipple={false}
                      disableFocusRipple
                      className="project-actions-menu-button"
                      aria-label={t`More actions for ${project.name}`}
                      aria-controls={isProjectMenuOpen && menuProject?.id === project.id ? 'project-actions-menu' : undefined}
                      aria-haspopup="menu"
                      aria-expanded={isProjectMenuOpen && menuProject?.id === project.id ? 'true' : undefined}
                      onClick={(event) => handleOpenProjectMenu(event, project)}
                      disabled={isRenaming}
                    >
                      <SettingsOutlinedIcon />
                    </IconButton>
                  </div>
              </article>
              );
            })
          )}
        </div>
      </div>

      <Menu
        id="project-actions-menu"
        anchorEl={projectMenuAnchor}
        open={isProjectMenuOpen && Boolean(menuProject)}
        onClose={handleCloseProjectMenu}
        slotProps={{
          paper: { className: 'project-actions-menu' },
          transition: { onExited: handleProjectMenuExited },
        }}
      >
        {menuProject && (menuProject.permission_tier === 1 ? [
          <MenuItem key="rename" onClick={() => { if (menuProject) handleOpenRenameDialog(menuProject); handleCloseProjectMenu(); }}>
            <EditRoundedIcon aria-hidden="true" /> {t`Rename`}
          </MenuItem>,
          <Divider key="owner-actions-divider" />,
          <MenuItem key="delete" className="project-actions-menu-delete" onClick={() => { if (menuProject) handleDeleteProject(menuProject.id); handleCloseProjectMenu(); }}>
            <DeleteOutlineRoundedIcon aria-hidden="true" /> {t`Delete`}
          </MenuItem>,
        ] : <MenuItem className="project-actions-menu-leave" onClick={() => { if (menuProject) handleLeaveProject(menuProject.id); handleCloseProjectMenu(); }}>
          <ExitToAppRoundedIcon aria-hidden="true" /> {t`Leave project`}
        </MenuItem>)}
      </Menu>

      <ProjectCreationWizard
        open={createDialogOpen}
        projects={projects}
        onClose={handleCloseCreateDialog}
        onProjectCreated={handleCreatedProject}
      />

      <Dialog open={Boolean(renameDialog)} onClose={handleCloseRenameDialog} className="project-rename-dialog" maxWidth="xs" fullWidth>
        <DialogTitle>{t`Rename Project`}</DialogTitle>
        <form onSubmit={handleRenameProjectSubmit}>
          <DialogContent>
            <TextField
              autoFocus
              fullWidth
              label={t`Project name`}
              value={renameDialog?.draftName ?? ''}
              onChange={(e) => setRenameDialog((prev) => prev ? { ...prev, draftName: e.target.value } : prev)}
              disabled={Boolean(renamingProjectId)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseRenameDialog} disabled={Boolean(renamingProjectId)}>{t`Cancel`}</Button>
            <Button type="submit" variant="contained" disabled={!renameDialog || renamingProjectId === renameDialog.projectId || !renameDialog.draftName.trim() || renameDialog.draftName.trim() === renameDialog.currentName}>
              {renamingProjectId ? t`Saving...` : t`Save`}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </div>
  );
};

export default ProjectSelector;
